import type {
  PersonalInfo,
  Experience,
  Education,
  SkillCategory,
  Project,
  SocialLink,
} from '../types'

export const personalInfo: PersonalInfo = {
  name: 'Francesc Leveque',
  title: 'Senior Software Engineer',
  tagline: 'Ruby on Rails | Elixir/Phoenix | Former Startup CTO',
  email: 'francesc@leveque.es',
  location: 'Cubelles, Catalonia, Spain',
  about: `Experienced software engineer with a strong foundation in Ruby on Rails, Elixir/Phoenix, Scala, PHP, and Python. Over the years, I've worked in both freelance and full-time roles, delivering robust and scalable solutions across a variety of industries. I've held CTO roles at small startups, leading product development, defining technical direction, and driving projects from concept to deployment. I'm especially interested in finance, investments, e-commerce, sustainability, green energy, travel, pets, and music.`,
}

export const experiences: Experience[] = [
  {
    id: '1',
    company: 'Factorial',
    position: 'Software Product Engineer',
    location: 'Barcelona, Spain',
    startDate: '2025-10',
    endDate: null,
    description: 'Building HR software solutions at one of Europe\'s leading HR tech companies, as a Full Stack Engineer',
    achievements: [],
    technologies: ['Ruby on Rails', 'React', 'TypeScript'],
  },
  {
    id: '2',
    company: 'Stuart',
    position: 'Senior Back End Engineer',
    location: 'Remote',
    startDate: '2023-01',
    endDate: '2025-03',
    description: 'Ruby on Rails, Elixir/Phoenix & Scala developer at a leading last-mile delivery platform.',
    achievements: [
      'Designed and implemented new features and microservices, improving scalability and maintainability',
      'Refactored legacy components, contributing to the migration from a monolithic architecture to distributed services',
      'Participated in on-call rotations, ensuring high availability and rapid incident resolution',
      'Mentored junior developers, fostering best practices and knowledge sharing within the team',
    ],
    technologies: ['Ruby on Rails', 'Elixir', 'Phoenix', 'Scala', 'Microservices'],
  },
  {
    id: '3',
    company: 'Stuart',
    position: 'Software Engineer',
    location: 'Remote',
    startDate: '2020-04',
    endDate: '2022-12',
    description: 'Ruby on Rails, Elixir/Phoenix & Scala developer.',
    achievements: [],
    technologies: ['Ruby on Rails', 'Elixir', 'Phoenix', 'Scala'],
  },
  {
    id: '4',
    company: 'Grup Ametller Origen',
    position: 'Software Engineer / Backend Developer',
    location: 'Olèrdola, Spain',
    startDate: '2017-08',
    endDate: '2020-04',
    description: 'Developed and maintained multiple web projects focused on e-commerce and corporate websites.',
    achievements: [
      'Led the maintenance and enhancement of the Magento-based e-commerce platform for 2 years',
      'Contributed to migration from Magento to VTEX, including key system integrations',
      'Coordinated the technical work of external providers across various web projects',
      'Expanded responsibilities to the Integrations and Microservices team, developing services with Scala',
    ],
    technologies: ['Magento', 'VTEX', 'Scala', 'PHP', 'Microservices'],
  },
  {
    id: '5',
    company: 'Crisalix',
    position: 'Full Stack Ruby on Rails Developer',
    location: 'Barcelona, Spain',
    startDate: '2014-06',
    endDate: '2017-08',
    description: 'Full Stack web developer on Ruby on Rails for medical imaging software.',
    achievements: [
      'Developed new features across a suite of web applications built with Ruby on Rails and Sinatra, hosted on AWS',
      'Designed and maintained internal and public APIs enabling seamless communication between applications',
      'Integrated third-party services including Datatrans payment platform',
      'Contributed to system automation and scalability using Nginx, Unicorn, Vagrant, and Puppet',
    ],
    technologies: ['Ruby on Rails', 'Sinatra', 'MySQL', 'AWS', 'RSpec', 'Capistrano'],
  },
  {
    id: '6',
    company: 'Cellove Social Network',
    position: 'Freelance Full Stack Ruby on Rails Developer',
    location: 'Vilafranca del Penedès, Spain',
    startDate: '2014-02',
    endDate: '2014-06',
    description: 'Collaborated on the development of cellove.com, a social networking platform for dating.',
    achievements: [
      'Improved and maintained core features using Ruby on Rails, jQuery, MariaDB, and Nginx',
      'Automated deployment and configuration with Ansible and Capistrano',
      'Integrated third-party services including Stripe for payments and Mandrill for transactional emails',
    ],
    technologies: ['Ruby on Rails', 'jQuery', 'MariaDB', 'Nginx', 'Ansible', 'Stripe'],
  },
  {
    id: '7',
    company: 'Spotlio',
    position: 'Freelance Full Stack Developer RoR / Symfony',
    location: 'Vilanova i la Geltrú, Spain',
    startDate: '2012-06',
    endDate: '2014-06',
    description: 'Designed and implemented web application frontend and backend services.',
    achievements: [
      'Designed and implemented a web application frontend using Ruby on Rails, HTML5, CSS3, and jQuery',
      'Integrated the application with a Symfony-based API, also consumed by mobile devices',
      'Refactored and optimized legacy backend components in Symfony 1.4',
      'Collaborated on building a new technology stack based on Symfony2 and Node.js',
      'Managed version control with Git and performed sysadmin tasks on AWS servers',
    ],
    technologies: ['Ruby on Rails', 'Symfony', 'Symfony2', 'Node.js', 'AWS', 'Git'],
  },
  {
    id: '8',
    company: 'Hogarius',
    position: 'IT Director',
    location: 'Sant Cugat Sesgarrigues, Spain',
    startDate: '2012-05',
    endDate: '2013-09',
    description: 'Led technical initiatives for a home retail franchise.',
    achievements: [
      'Designed and developed the company\'s website using Ruby on Rails, integrating it with Magento and OpenERP',
      'Served as the technical point of contact for clients, gathering requirements and translating them into solutions',
      'Contributed to digital initiatives including information kiosks, mobile applications, and Kinect-based solutions',
      'Managed version control with Git and maintained dedicated server hosting intranet, e-commerce, and OwnCloud',
    ],
    technologies: ['Ruby on Rails', 'Magento', 'OpenERP', 'Git', 'Linux'],
  },
  {
    id: '9',
    company: 'n\'êventum',
    position: 'Backend Software Engineer Freelance',
    location: 'Barcelona, Spain',
    startDate: '2011-04',
    endDate: '2012-05',
    description: 'Developed web solutions and API integrations.',
    achievements: [
      'Developed web solutions using Lemur Framework, PHP, MySQL, and MongoDB',
      'Integrated external services via APIs, including Google Maps and Highrise',
      'Managed data migration and synchronization from Drupal using XML and scheduled cron jobs',
      'Maintained version control and collaborative workflows with Git',
    ],
    technologies: ['PHP', 'MySQL', 'MongoDB', 'Git', 'APIs'],
  },
  {
    id: '10',
    company: 'Quantic',
    position: 'Owner / Freelance Software Engineer',
    location: 'Vilanova i la Geltrú, Spain',
    startDate: '2003-04',
    endDate: '2014-06',
    description: 'Delivered full-cycle web development projects as a freelance consultant.',
    achievements: [
      'Built solutions using LAMP stack and Ruby on Rails, with expertise in Java, Python, and multiple frameworks (Symfony2, Yii, CakePHP)',
      'Built and customized CMS and e-commerce platforms including Magento, Drupal, Joomla, and ezPublish',
      'Collaborated with design studios and companies on medium- and long-term projects',
      'Launched and managed personal projects including a leading Spanish-speaking parrot community (later sold) and a profitable online pet accessories store',
    ],
    technologies: ['Ruby on Rails', 'PHP', 'Symfony', 'Magento', 'Drupal', 'Python', 'MySQL'],
  },
  {
    id: '11',
    company: 'Lavinia TC',
    position: 'Web Developer & Team Lead',
    location: 'Spain',
    startDate: '2002-01',
    endDate: '2003-12',
    description: 'Analyzed and developed websites, managing small development teams.',
    achievements: [
      'Analyzed and developed websites using DHTML, PHP, and JSP',
      'Managed small development teams (1-4 programmers), coordinating tasks and ensuring project delivery',
    ],
    technologies: ['PHP', 'JSP', 'DHTML'],
  },
  {
    id: '12',
    company: 'Neotec, Nuevas Tecnologías SL',
    position: 'Python Web Developer',
    location: 'Spain',
    startDate: '2002-01',
    endDate: '2002-12',
    description: 'Developed the Port Authority of Barcelona (APB) website.',
    achievements: [
      'Analyzed and developed the APB website using XML, XSLT, DTDs, and Python as part of a small team',
      'Managed content workflows and automated document conversion from Word to XML',
      'Developed and maintained web applications on the Zope platform',
    ],
    technologies: ['Python', 'Zope', 'XML', 'XSLT'],
  },
  {
    id: '13',
    company: 'Interweb Research',
    position: 'CTO',
    location: 'Spain',
    startDate: '2000-01',
    endDate: '2002-12',
    description: 'Led the technical team at an early-stage web development company.',
    achievements: [
      'Led the technical team, managing programmers and coordinating project delivery',
      'Acted as the primary technical contact for external companies collaborating on projects',
      'Administered company servers (6 Linux, 1 Windows NT), including Apache, Tomcat, PHP, Oracle clients',
      'Developed web solutions using DHTML, PHP, Java, ASP, and MySQL',
    ],
    technologies: ['PHP', 'Java', 'MySQL', 'Linux', 'Apache', 'Tomcat'],
  },
  {
    id: '14',
    company: 'Interweb Research',
    position: 'Web Developer & Systems Administrator',
    location: 'Spain',
    startDate: '1999-01',
    endDate: '2000-12',
    description: 'Developed web applications and administered Linux servers.',
    achievements: [
      'Developed web applications using DHTML, PHP, Perl, ASP, XML/XSL, MySQL, and PostgreSQL',
      'Administered Linux servers, ensuring uptime and system reliability',
      'Provided technical support to clients, resolving issues and ensuring smooth operation of web services',
    ],
    technologies: ['PHP', 'Perl', 'MySQL', 'PostgreSQL', 'Linux', 'XML'],
  },
  {
    id: '15',
    company: 'OLR Net',
    position: 'Front-End Developer',
    location: 'Spain',
    startDate: '1999-01',
    endDate: '1999-12',
    description: 'Developed dynamic web pages using DHTML and related web technologies.',
    achievements: [],
    technologies: ['DHTML', 'JavaScript', 'HTML', 'CSS'],
  },
]

export const education: Education[] = [
  {
    id: '1',
    institution: 'Universitat Oberta de Catalunya',
    degree: 'Bachelor\'s Degree',
    field: 'Computer Science - Software Engineering',
    location: 'Catalonia, Spain',
    startDate: '2016',
    endDate: '',
    description: 'Grado de Ingeniería Informática, specialization in Software Engineering.',
  },
  {
    id: '2',
    institution: 'Universitat Autònoma de Barcelona',
    degree: 'Technical Engineering',
    field: 'Computer Systems Engineering',
    location: 'Barcelona, Spain',
    startDate: '1992',
    endDate: '1997',
    description: 'Ingeniería técnica en informática de sistemas.',
  },
]

export const skillCategories: SkillCategory[] = [
  {
    id: 'languages',
    name: 'Languages',
    skills: [
      { name: 'Ruby', level: 'expert' },
      { name: 'Elixir', level: 'advanced' },
      { name: 'TypeScript', level: 'advanced' },
      { name: 'Scala', level: 'intermediate' },
      { name: 'Python', level: 'intermediate' },
      { name: 'PHP', level: 'advanced' },
      { name: 'SQL', level: 'advanced' },
    ],
  },
  {
    id: 'frameworks',
    name: 'Frameworks',
    skills: [
      { name: 'Ruby on Rails', level: 'expert' },
      { name: 'Phoenix', level: 'advanced' },
      { name: 'React', level: 'advanced' },
      { name: 'Hotwire', level: 'intermediate' },
      { name: 'Symfony', level: 'intermediate' },
    ],
  },
  {
    id: 'tools',
    name: 'Tools & Infrastructure',
    skills: [
      { name: 'Git', level: 'expert' },
      { name: 'Docker', level: 'advanced' },
      { name: 'Kubernetes', level: 'intermediate' },
      { name: 'PostgreSQL', level: 'advanced' },
      { name: 'MySQL', level: 'advanced' },
      { name: 'AWS', level: 'intermediate' },
      { name: 'Linux', level: 'advanced' },
    ],
  },
  {
    id: 'practices',
    name: 'Practices & Certifications',
    skills: [
      { name: 'Microservices Architecture' },
      { name: 'Test-Driven Development' },
      { name: 'CI/CD' },
      { name: 'Agile/Scrum' },
      { name: 'Google Kubernetes Engine' },
      { name: 'Full-Stack Phoenix' },
    ],
  },
]

export const projects: Project[] = [
  {
    id: '7',
    name: 'Quantic Finance',
    description: 'AI-enhanced dividend investing platform — a single Elixir/Phoenix LiveView monolith that consolidated a former multi-stack ecosystem (Rails, Phoenix, Go, NestJS) into one app: portfolio, radar, dividends, buy plan, FIRE projections, community pages, a Telegram bot, and AI insights.',
    technologies: ['Elixir', 'Phoenix', 'LiveView', 'PostgreSQL', 'AI Enhanced'],
    url: 'https://quantic.finance',
  },
  {
    id: '8',
    name: 'yahoo_finance_ex',
    description: 'Elixir client for Yahoo Finance — single and batched stock quotes, FX rates, asset profiles, and dividend history, with Yahoo\'s cookie + CSRF crumb auth handled transparently. Published on Hex.',
    technologies: ['Elixir', 'Hex', 'REST API'],
    githubUrl: 'https://github.com/fleveque/yahoo_finance_ex',
    url: 'https://hex.pm/packages/yahoo_finance_ex',
  },
  {
    id: '1',
    name: 'Dividend Portfolio Tracker',
    description: 'AI-enhanced application to track dividend investments and analyze portfolio performance with intelligent insights.',
    technologies: ['Ruby on Rails', 'React', 'TypeScript', 'AI Enhanced'],
    githubUrl: 'https://github.com/fleveque/dividend-portfolio',
  },
  {
    id: '2',
    name: 'Pulse',
    description: 'Real-time community dividend portfolio dashboard with live updates, event-driven sync, and shared portfolio views.',
    technologies: ['Elixir', 'Phoenix', 'Distributed Systems', 'Event-Driven'],
    githubUrl: 'https://github.com/fleveque/pulse',
  },
  {
    id: '3',
    name: 'Logo Service',
    description: 'Self-hosted microservice for stock ticker logos with local caching, GitHub imports, and AI-powered web search.',
    technologies: ['Go', 'SQLite', 'Docker', 'REST API'],
    githubUrl: 'https://github.com/fleveque/logo-service',
  },
  {
    id: '4',
    name: 'Yahoo Finance Client',
    description: 'Ruby gem for fetching stock data from Yahoo Finance API. Published on RubyGems.',
    technologies: ['Ruby', 'RSpec', 'REST API', 'RubyGems'],
    githubUrl: 'https://github.com/fleveque/yahoo_finance_client',
  },
  {
    id: '5',
    name: 'Personal Website',
    description: 'This personal website built with Ruby on Rails and React, showcasing my CV and projects.',
    technologies: ['Ruby on Rails', 'React', 'TypeScript', 'Tailwind CSS'],
    url: 'https://leveque.es',
  },
  {
    id: '6',
    name: 'Open Source Contributions',
    description: 'Contributor to various open source projects in the Ruby and Elixir ecosystems.',
    technologies: ['Ruby', 'Elixir', 'Rails', 'Phoenix'],
    url: 'https://github.com/fleveque',
  },
]

export const socialLinks: SocialLink[] = [
  {
    id: 'github',
    name: 'GitHub',
    url: 'https://github.com/fleveque',
    icon: 'github',
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/in/fleveque',
    icon: 'linkedin',
  },
  {
    id: 'email',
    name: 'Email',
    url: 'mailto:francesc@leveque.es',
    icon: 'email',
  },
]
